/*
 *
 * LanguageProvider
 *
 * this component connects the redux state language locale to the
 * IntlProvider component and i18n messages (loaded from `app/translations`)
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import injectReducer from 'utils/injectReducer';
import injectSaga from 'utils/injectSaga';
import { createSelector } from 'reselect';
import { IntlProvider } from 'react-intl';
import { TransitionGroup } from 'react-transition-group';
import FullscreenLoading from 'ui/FullscreenLoading';
import FullscreenError from 'ui/FullscreenError';
import Fade from 'ui/Fade';
import FontProvider from 'ui/FontProvider';

import { selectLanguage } from './selectors';
import { changeLanguage } from './actions';

import reducer from './reducer';
import saga from './saga';

export class LanguageProvider extends React.Component { // eslint-disable-line react/prefer-stateless-function
  UNSAFE_componentWillMount() {
    this.props.dispatch(changeLanguage(this.props.country, this.props.language));
  }

  render() {
    const {
      languageState: { messages, loading, error, language, locale },
      country,
      children,
    } = this.props;

    return (
      <TransitionGroup>
        { error && <Fade inProps={!!error}>
          <FullscreenError>Country / Language combination not found.</FullscreenError>
        </Fade>}
        <Fade inProps={loading && !error}>
          {loading && !error ? <FullscreenLoading id={'fullscreen-loading'} /> : null}
        </Fade>

        <Fade inProps={!loading && !error}>
          {!loading && !error ? <IntlProvider 
            defaultLocale={'en-SE'} locale={locale} key={language} messages={messages}
            onError={(err) => {
              if (err.code === "MISSING_TRANSLATION") {
                console.warn("Missing translation", err.message);
                return;
              }
            }}
          >
            <FontProvider country={country} language={language}>
              {React.Children.only(children)}
            </FontProvider>
          </IntlProvider> : null}
        </Fade>
      </TransitionGroup>
    );
  }
}

LanguageProvider.propTypes = {
  children: PropTypes.element.isRequired,
  dispatch: PropTypes.func.isRequired,
  language: PropTypes.string.isRequired,
  country: PropTypes.string.isRequired,
  languageState: PropTypes.object.isRequired,
};

const mapStateToProps = createSelector(
  selectLanguage,
  (state) => ({ languageState: state.toJS() })
);

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);
const withReducer = injectReducer({ key: 'language', reducer });
const withSaga = injectSaga({ key: 'language', saga });

export default compose(
  withReducer,
  withSaga,
  withConnect,
)(LanguageProvider);
